import React from 'react';
import { FormattedMessage } from 'react-intl';
import { call } from './../utilities/http';
import {
    LOADING,
    COMPLETE,
    SET_PROGRESS,
    DOCUMENTS_SET_LOADING_TYPE,
    DOCUMENTS_UNSET_LOADING_TYPE,
    DOCUMENTS_GET_ALL_OK,
    DOCUMENTS_CLEAR_ALL_OK,
    DOCUMENTS_UNSET_ID,
    DOCUMENTS_GET_SIGNATURES_OK,
    DOCUMENTS_SET_REPLACE_DOC,
    CREATED_I9,
    CREATED_JOBDOC,
    SAVING
} from './types';
import { Snackbar } from '../components/snackbar';

const defaultState = {
    pending: undefined,
    all: undefined,
    signatures: undefined,
    replaceDocument: undefined
};

/**
 * Loading state mapper.
 * @param {object} state
 * @param {string} action
 */
export default function(state = defaultState, action) {
    switch (action.type) {
        case DOCUMENTS_SET_LOADING_TYPE:
            if (isBeingLoaded(state, action.data)) {
                return { ...state };
            }
            return { ...state, pending: state.pending ? [...state.pending, action.data] : [action.data] };

        case DOCUMENTS_UNSET_LOADING_TYPE:
            if (isBeingLoaded(state, action.data)) {
                return { ...state, pending: state.pending ? [...state.pending.filter(item => item !== action.data)] : [] };
            }

            return { ...state };

        case DOCUMENTS_CLEAR_ALL_OK:
            return { ...state, all: undefined };

        case DOCUMENTS_GET_ALL_OK:
            if (action.propName) {
                let docs = state[action.propName];
                state[action.propName] = { ...docs, list: action.data };
                return { ...state };
            }
            return { ...state, all: action.data };

        case DOCUMENTS_UNSET_ID:
            let docs = { ...state };
            if (docs[action.data]) {
                delete docs[action.data];
            }
            return { ...docs };

        case DOCUMENTS_GET_SIGNATURES_OK:
            return { ...state, signatures: action.data };

        case CREATED_I9:
            return { ...state, [`form${action.docType}`]: action.response, createdForm: action.docType, [action.id]: { list: [action.response] } };

        // TODO - does offer need the same functionality as I9 above?
        case CREATED_JOBDOC:
            return { ...state };

        case DOCUMENTS_SET_REPLACE_DOC:
            if (action.propName) {
                let docs = state[action.propName];
                state[action.propName] = { ...docs, replaceDocument: action.data };
                return { ...state };
            }
            return { ...state, replaceDocument: action.data };

        default:
            return state;
    }
}

export function createSignRequest(name, refType, refId, docId, onSuccess = () => null, onError = () => null) {
    let body = {
        name: name,
        ref_type: refType,
        ref_id: refId,
        doc_id: docId
    };

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call({ path: '/esign', method: 'POST', body: body })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.documentSignatureInitialize'} defaultMessage={'Unable to initialize signing process.'} />);
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function getSigningUrl(signatureId, onSuccess = response => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/esign/${signatureId}/url` })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.documentSignatureInitialize'} defaultMessage={'Unable to initialize signing process.'} />);
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function getDocumentDetails(documentId, queryParams, onSuccess = () => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/doc/${documentId}`, queryParams })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.documentDetails'} defaultMessage={'Unable to retrieve document details.'} />);
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function loadDocuments(role, onSuccess = () => null) {
    const { type, id } = role;

    return async function(dispatch) {
        dispatch({ type: DOCUMENTS_CLEAR_ALL_OK });
        dispatch({ type: LOADING });
        call({ path: `/doc?ref_type=${type}&ref_id=${id}&extended=true` })
            .then(response => {
                dispatch({ type: DOCUMENTS_GET_ALL_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                dispatch({ type: COMPLETE });
            });
    };
}

export function getDocumentsForce(role, onSuccess, onError, propName, filter) {
    return getDocuments(role, onSuccess, onError, propName, filter, true);
}

export function getDocuments(role, onSuccess = () => null, onError = error => null, propName = undefined, filter = undefined, force = false) {
    const { type, id, includeAll } = role;
    let hash = JSON.stringify(role);

    return async function(dispatch, getState) {
        const { documents } = getState();
        if (isBeingLoaded(documents, hash)) return;
        const all = documents[`${id}_all`];
        if (all && !force) {
            dispatch({ type: DOCUMENTS_GET_ALL_OK, data: all.filter(filter), propName: propName });
            return;
        }

        dispatch({ type: LOADING });
        dispatch({ type: DOCUMENTS_SET_LOADING_TYPE, data: hash });
        call({ path: `/doc?ref_type=${type}&ref_id=${id}&extended=true&include_all=${!!includeAll}` })
            .then(response => {
                dispatch({ type: DOCUMENTS_GET_ALL_OK, data: response, propName: `${id}-all` });
                let documents = filter ? response.filter(filter) : response;
                dispatch({ type: DOCUMENTS_GET_ALL_OK, data: documents, propName: propName });

                dispatch({ type: DOCUMENTS_UNSET_LOADING_TYPE, data: hash });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.documents'} defaultMessage={'Unable to retrieve company documents.'} />);
                dispatch({ type: DOCUMENTS_UNSET_LOADING_TYPE, data: hash });
                dispatch({ type: DOCUMENTS_GET_ALL_OK, data: [{ error: true }], propName: propName });
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function isBeingLoaded(documents, type) {
    const { pending } = documents;
    if (!pending || pending.length === 0) {
        return false;
    }

    return pending.find(item => item === type) !== undefined;
}

export function unsetDocumentId(id, onSuccess = () => null) {
    return async function(dispatch) {
        dispatch({ type: DOCUMENTS_UNSET_ID, data: id });
        onSuccess(id);
    };
}

export function getSignatures(role, onSuccess = () => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/esign?ref_type=${role.type}&ref_id=${role.id}` })
            .then(response => {
                dispatch({ type: DOCUMENTS_GET_SIGNATURES_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.documentSignatures'} defaultMessage={'Unable to retrieve document signatures.'} />);
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function createDocument(document, onSuccess = response => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/doc`, method: 'POST', body: document })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function createDocuments(documens, onSuccess = response => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/docs`, method: 'POST', body: documens })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function createI9Form(contractorUserId, contractorId, adminId, docType, body, onSuccess = () => null, onError = () => null) {
    const queryParams = {
        user: contractorUserId,
        output_ref_type: 'contractor',
        output_ref_id: contractorId,
        blank_ref_type: 'admin',
        blank_ref_id: adminId,
        doc_type: 'USA-I9',
        use_new_docs: true
    };
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${contractorUserId}/make_pdf`, method: 'POST', queryParams, body })
            .then(response => {
                dispatch({ type: CREATED_I9, docType, id: response.id, response: { ...response, display_name: `Pdf for List ${docType}` } });
                dispatch({ type: COMPLETE });
                onSuccess();
            })
            .catch(error => {
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function createOfferLetter(jobId, contractorId, esign_id = undefined, onSuccess = () => null, onError = () => null) {
    return createJobDocument(jobId, contractorId, 'offer-letter2', esign_id, onSuccess, onError);
}

export function createAssignmentW2(jobId, contractorId, esign_id = undefined, onSuccess = () => null, onError = () => null) {
    return createJobDocument(jobId, contractorId, 'assignment', esign_id, onSuccess, onError);
}

export function createAssignmentIC(jobId, contractorId, esign_id = undefined, onSuccess = () => null, onError = () => null) {
    return createJobDocument(jobId, contractorId, 'assignmentIC', esign_id, onSuccess, onError);
}

export function createICAgreement(jobId, contractorId, esign_id = undefined, onSuccess = () => null, onError = () => null) {
    return createJobDocument(jobId, contractorId, 'ic-agreement', esign_id, onSuccess, onError);
}

function createJobDocument(jobId, contractorId, doc_type, esign_id = undefined, onSuccess = () => null, onError = () => null) {
    const queryParams = {
        doc_type,
        job: jobId,
        output_ref_type: 'contractor',
        output_ref_id: contractorId,
        esign_id,
        use_new_docs: 'true'
    };
    return async function(dispatch) {
        call({ path: `/job/${jobId}/make_pdf`, method: 'POST', queryParams })
            .then(response => {
                dispatch({ type: CREATED_JOBDOC, data: response });
                onSuccess();
            })
            .catch(error => {
                onError(error);
            });
    };
}

export function upload(file, s3url, onSuccess = () => null, id = undefined, onProgress = () => null) {
    let listeners = {
        progress: (event, dispatch) => {
            if (event.lengthComputable) {
                let value = Math.round(event.loaded * 100 / event.total);

                if (id && onProgress) {
                    onProgress(id, value);
                } else {
                    let progress = (
                        <FormattedMessage
                            id="fileUpload.progress"
                            defaultMessage={'{filename}, {percent}% uploaded.'}
                            values={{ filename: file.name, percent: value }}
                        />
                    );

                    dispatch({ type: SET_PROGRESS, data: progress });
                }
            }
        },
        load: (response, dispatch) => {
            Promise.resolve(response);
            onSuccess();
            if (!id || !onProgress) {
                dispatch({ type: COMPLETE });
            }
        },
        error: (error, dispatch) => {
            Promise.reject(error);
            if (!id || !onProgress) {
                dispatch({ type: COMPLETE });
            }
        }
    };

    return async function(dispatch) {
        if (!id || !onProgress) {
            dispatch({ type: LOADING });
        }

        const request = new XMLHttpRequest();
        request.open('PUT', s3url);

        if (!request.upload) {
            request.addEventListener('progress', event => listeners.progress(event, dispatch));
            request.addEventListener('load', response => listeners.load(response, dispatch));
            request.addEventListener('error', error => listeners.error(error, dispatch));
        } else {
            request.upload.addEventListener('progress', event => listeners.progress(event, dispatch));
            request.upload.addEventListener('load', response => listeners.load(response, dispatch));
            request.upload.addEventListener('error', error => listeners.error(error, dispatch));
        }

        request.overrideMimeType(file.type);
        request.send(file);
    };
}

export function archiveDocument(documentId, onSuccess = response => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/doc/${documentId}`, method: 'DELETE' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function restoreDocument(documentId, onSuccess = response => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/doc/${documentId}/action/pend`, method: 'POST' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function deleteDocument(documentId, onSuccess = response => null, onError = error => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/doc/${documentId}`, method: 'DELETE' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

export function setReplaceDocument(document, id = undefined) {
    return async function(dispatch) {
        dispatch({ type: DOCUMENTS_SET_REPLACE_DOC, data: document, propName: id });
    };
}



// WEBPACK FOOTER //
// ./src/reducers/documents.jsx